import { fileService } from "@/services/ServiceRegistry";
import { ReservationUpdateFormData } from "../guests/utils/reservationSchema";
import { UpdateReservationRequest } from "@/types/reservation";
import { useUpdateReservation } from "@/hooks/useReservations";
import { useDocumentCreate } from "@/hooks/useDocumentCreate";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

export const useReservationUpdate = (id: string) => {
  const navigate = useNavigate();
  const updateReservationMutation = useUpdateReservation(id);
  const { handleDocumentCreate } = useDocumentCreate();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReservationUpdate = async (
    data: ReservationUpdateFormData,
    oldReservation: ReservationUpdateFormData,
    selectedPhotoFile: File | null,
    existingPhotoId?: string | null,
  ) => {
    try {
      const differentReservationData: ReservationUpdateFormData =
        Object.fromEntries(
          Object.entries(data).filter(([key, value]) => {
            return (
              value !== oldReservation[key as keyof ReservationUpdateFormData]
            );
          }),
        ) as ReservationUpdateFormData;

      setIsLoading(true);
      const {
        firstName,
        lastName,
        language,
        phone,
        comments,
        documents,
        roomId,
        estimatedTimeOfArrival,
        preCheckinEnteredGuests,
        arrivalDate,
        departureDate,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        additionalGuests,
        ...bookingData
      } = differentReservationData;

      let photoFileId = existingPhotoId;

      // Handle photo upload/update
      if (selectedPhotoFile) {
        try {
          if (existingPhotoId) {
            // Update existing photo
            await fileService.updateFileWithPresignedUrl(
              existingPhotoId,
              selectedPhotoFile,
            );
            photoFileId = existingPhotoId;
          } else {
            // Upload new photo
            photoFileId =
              await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
          }
        } catch (photoError) {
          console.error("Failed to upload/update photo:", photoError);
        }
      }

      // Prepare update request - simplified for now
      const updateRequest: UpdateReservationRequest = {
        roomId: roomId,
        ...(arrivalDate &&
          departureDate && {
            arrivalDate: new Date(new Date(arrivalDate).setHours(14, 0, 0, 0)),
            departureDate: new Date(
              new Date(departureDate).setHours(12, 0, 0, 0),
            ),
          }),
        ...(preCheckinEnteredGuests && {
          preCheckinEnteredGuests: Number(preCheckinEnteredGuests),
        }),
        ...(estimatedTimeOfArrival && {
          estimatedTimeOfArrival: new Date(estimatedTimeOfArrival),
        }),
        ...(photoFileId &&
          photoFileId !== existingPhotoId && {
            photoId: photoFileId,
          }),
        firstName,
        lastName,
        phone,
        language,
        checkinComments: comments,
        ...bookingData,
        // should be added on backend as well, then it can be uncommented
        // ...(data.additionalGuests &&
        //   data.additionalGuests.length > 0 && {
        //     additionalGuests: data.additionalGuests.map((g) => ({
        //       guestType: g.guestType,
        //       firstName: g.firstName,
        //       lastName: g.lastName,
        //       email: g.email,
        //       phone: g.phone,
        //       language: g.language,
        //       ...(g.dateOfBirth ? { dateOfBirth: g.dateOfBirth } : {}),
        //     })),
        //   }),
      };

      await updateReservationMutation.mutateAsync(updateRequest);

      // Handle document uploads if any new documents
      if (documents && documents.length > 0) {
        await Promise.all(
          documents
            .filter((d) => !d.id)
            .map(async (document) => {
              if (!document.file) {
                console.error("Missing file for document");
                return;
              }

              await handleDocumentCreate({
                ...document,
                file: document.file!,
                guestId: data.guestId!,
                bookingId: id!,
              });
            }),
        );
      }

      toast.success("Reservation updated successfully");
      navigate("/reservations");
    } catch (error) {
      toast.error((error as Error).message);
      setError((error as Error).message);
      console.error("Error updating reservation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleReservationUpdate,
    isLoading,
    error,
  };
};
